// src/App.js
import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { KioskDraftProvider } from './context/KioskDraftContext';
import { ThemeProvider } from './context/ThemeContext';

// Screens
import LandingPage from './screens/LandingPage';
import Home from './screens/Home';
import VehicleCatalogScreen from './screens/VehicleCatalogScreen';
import ProfileScreen from './screens/ProfileScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import VerifyEmailScreen from './screens/VerifyEmailScreen';
import WaiverScreen from './screens/WaiverScreen';
import AppointmentScreen from './screens/AppointmentScreen';
import AdminDealersScreen from './screens/AdminDealersScreen';
import DetailScreen from './screens/DetailScreen';
import AppointmentsScreen from './screens/AppointmentsScreen';
import FavoritesScreen from './screens/FavoritesScreen';
import DealerDashboardScreen from './screens/DealerDashboardScreen';
import DealerSlotsScreen from './screens/DealerSlotsScreen';
import DealerCalendarScreen from './screens/DealerCalendarScreen';
import AppointmentDetailScreen from './screens/AppointmentDetailScreen';
import KioskHomeScreen from './screens/KioskHomeScreen';
import KioskCheckInScreen from './screens/KioskCheckInScreen';
import KioskAppointmentSearchScreen from './screens/KioskAppointmentSearchScreen';
import KioskAppointmentDetailScreen from './screens/KioskAppointmentDetailScreen';
import KioskProfileScreen from './screens/KioskProfileScreen';
import KioskVehicleCatalogScreen from './screens/KioskVehicleCatalogScreen';
import KioskCustomerInfoScreen from './screens/KioskCustomerInfoScreen';
import KioskSlotSelectionScreen from './screens/KioskSlotSelectionScreen';
import KioskAppointmentConfirmationScreen from './screens/KioskAppointmentConfirmationScreen';
import KioskAppointmentSuccessScreen from './screens/KioskAppointmentSuccessScreen';
import CheckInScreen from './screens/CheckInScreen';
import CustomerVerificationScreen from './screens/CustomerVerificationScreen';
import DealerSearchScreen from './screens/DealerSearchScreen';
import DealershipScreen from './screens/DealershipScreen';




// Layout components
import ResponsiveContainer from './components/ResponsiveContainer';
import TopNav from './components/TopNav';
import BottomNav from './components/BottomNav';
import CustomerTabNav from './components/CustomerTabNav';
import RouteGuard from './components/RouteGuard';

// Inner component that has access to AuthContext
const AppContent = () => {
  const { user } = useContext(AuthContext);
  // Show tabbed navigation for non-logged-in users and customers
  const showTabNav = !user || user.role === 'customer';

  return (
    <RouteGuard>
      <div className="flex flex-col min-h-screen lg:min-h-0">
        {/* Conditional Navigation based on user role */}
        {showTabNav ? (
          // Non-logged-in users and customers get tabbed navigation on all screen sizes
          <CustomerTabNav />
        ) : (
          // Other roles (dealer, kiosk, admin) get traditional TopNav
          <TopNav />
        )}

        {/* Main content area */}
        <div className="flex-1 overflow-y-auto lg:overflow-visible">
          <Routes>
               <Route path="/" element={<Home />} />
               <Route path="/old-landing" element={<LandingPage />} />
               <Route path="/dealers/search" element={<DealerSearchScreen />} />
               <Route path="/dealer/:id/info" element={<DealershipScreen />} />
               <Route path="/vehicles" element={<VehicleCatalogScreen />} />
                  <Route path="/vehicle/:id/waiver" element={<WaiverScreen />} />
                  <Route path="/vehicle/:id/appointment" element={<AppointmentScreen />} />
                  {/* Backward-compatibility route */}
                  <Route path="/vehicle/:id/book" element={<AppointmentScreen />} />
                {DetailScreen && (
                  <Route path="/vehicle/:id" element={<DetailScreen />} />
                )}
                {AppointmentsScreen && (
                  <Route path="/appointments" element={<AppointmentsScreen />} />
                )}
                {/* Backward-compatibility route */}
                {AppointmentsScreen && (
                  <Route path="/bookings" element={<Navigate to="/appointments" replace />} />
                )}
                {FavoritesScreen && (
                  <Route path="/favorites" element={<FavoritesScreen />} />
                )}
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/login" element={<LoginScreen />} />
                <Route path="/register" element={<RegisterScreen />} />
                <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
                <Route path="/reset-password" element={<ResetPasswordScreen />} />
                <Route path="/verify-email" element={<VerifyEmailScreen />} />

                <Route path="/admin/dealers" element={<AdminDealersScreen />} />

                <Route path="/dealer" element={<DealerDashboardScreen />} />

                <Route path="/dealer/slots" element={<DealerSlotsScreen />} />

                <Route path="/dealer/calendar" element={<DealerCalendarScreen />} />


                {/* Dealer customer verification */}
                <Route path="/dealer/verify/:id" element={<CustomerVerificationScreen />} />

                <Route path="/appointment/:id" element={<AppointmentScreen />} />
                {/* Backward-compatibility route */}
                <Route path="/booking/:id" element={<Navigate to={window.location.pathname.replace('/booking/', '/appointment/')} replace />} />

                <Route path="/appointments/:id" element={<AppointmentDetailScreen />} />
                {/* Backward-compatibility route */}
                <Route path="/bookings/:id" element={<Navigate to={window.location.pathname.replace('/bookings/', '/appointments/')} replace />} />

                {/* Check-in route (accessible to all authenticated users) */}
                <Route path="/checkin/:token" element={<CheckInScreen />} />

                {/* Kiosk routes */}
                <Route path="/kiosk" element={<KioskHomeScreen />} />
                <Route path="/kiosk/checkin" element={<KioskCheckInScreen />} />
                <Route path="/kiosk/search" element={<KioskAppointmentSearchScreen />} />
                <Route path="/kiosk/appointments/:id" element={<KioskAppointmentDetailScreen />} />
                {/* Backward-compatibility route */}
                <Route path="/kiosk/bookings/:id" element={<Navigate to={window.location.pathname.replace('/kiosk/bookings/', '/kiosk/appointments/')} replace />} />
                <Route path="/kiosk/profile" element={<KioskProfileScreen />} />

                {/* Kiosk appointment flow */}
                <Route path="/kiosk/book" element={<KioskVehicleCatalogScreen />} />
                <Route path="/kiosk/book/customer-info" element={<KioskCustomerInfoScreen />} />
                <Route path="/kiosk/book/select-slot" element={<KioskSlotSelectionScreen />} />
                <Route path="/kiosk/book/confirm" element={<KioskAppointmentConfirmationScreen />} />
                <Route path="/kiosk/book/success" element={<KioskAppointmentSuccessScreen />} />

          </Routes>
        </div>

        {/* Bottom nav – mobile/tablet only, hidden for non-logged-in users and customers */}
        {!showTabNav && <BottomNav />}
      </div>
    </RouteGuard>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <KioskDraftProvider>
            <ResponsiveContainer>
              <AppContent />
            </ResponsiveContainer>
          </KioskDraftProvider>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
